import { useCallback, useRef, type MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import type { MarkerPreceptor } from './types';

export const createTooltipHTML = (preceptor: MarkerPreceptor, options?: { hideName?: boolean }) => {
  const hideName = options?.hideName;
  const container = document.createElement('div');
  container.className = 'min-w-[240px] max-w-[280px] rounded-lg border border-slate-200 bg-white p-3 shadow-md text-slate-700 space-y-3';

  const headingBlock = document.createElement('div');
  headingBlock.className = 'space-y-1';

  if (!hideName) {
    const name = document.createElement('p');
    name.className = 'text-sm font-semibold text-slate-900';
    name.textContent = preceptor.name || `Preceptor ${preceptor.id}`;
    headingBlock.appendChild(name);
  }

  const rotation = document.createElement('p');
  rotation.className = hideName ? 'text-sm font-semibold text-slate-900' : 'text-xs uppercase tracking-wide text-slate-500';
  rotation.textContent = preceptor.rotationCode ? `Rotation ${preceptor.rotationCode}` : 'Rotation pending';
  headingBlock.appendChild(rotation);
  container.appendChild(headingBlock);

  const specialtyRow = document.createElement('div');
  specialtyRow.className = 'flex items-start gap-2 text-xs';
  const specialtyLabel = document.createElement('span');
  specialtyLabel.className = 'mt-[2px] font-semibold text-slate-500';
  specialtyLabel.textContent = 'Specialty';
  const specialtyValue = document.createElement('span');
  specialtyValue.className = 'text-slate-700 font-medium';
  const specialtySource = preceptor.specialty as string | { name?: string } | undefined;
  const specialty = typeof specialtySource === 'string'
    ? specialtySource
    : specialtySource?.name;
  specialtyValue.textContent = specialty || 'Not specified';
  specialtyRow.appendChild(specialtyLabel);
  specialtyRow.appendChild(specialtyValue);
  container.appendChild(specialtyRow);

  const locationRow = document.createElement('div');
  locationRow.className = 'flex items-start gap-2 text-xs';
  const locationLabel = document.createElement('span');
  locationLabel.className = 'mt-[2px] font-semibold text-slate-500';
  locationLabel.textContent = 'Location';
  const locationValue = document.createElement('span');
  locationValue.className = 'text-slate-700 font-medium truncate';
  const locationDisplay = [preceptor.city, preceptor.zip].filter(Boolean).join(' ').trim();
  locationValue.textContent = locationDisplay || 'Not provided';
  locationRow.appendChild(locationLabel);
  locationRow.appendChild(locationValue);
  container.appendChild(locationRow);

  if (preceptor.rotationCode) {
    const action = document.createElement('a');
    action.href = `/rotations/${encodeURIComponent(preceptor.rotationCode)}`;
    action.className = 'block w-full rounded-md bg-slate-900 px-3 py-2 text-center text-xs font-semibold text-white hover:bg-slate-800 transition-colors';
    action.textContent = 'Open rotation details';
    action.target = '_blank';
    action.rel = 'noreferrer';
    container.appendChild(action);
  }

  return container;
};

export const useMapTooltip = (mapRef: MutableRefObject<mapboxgl.Map | null>, showTooltips: boolean) => {
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const hideTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTooltip = useCallback(() => {
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
      hideTooltipTimeoutRef.current = null;
    }
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  }, []);

  const scheduleHideTooltip = useCallback(() => {
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
    }
    hideTooltipTimeoutRef.current = setTimeout(() => {
      clearTooltip();
    }, 200);
  }, [clearTooltip]);

  const showTooltipForPreceptor = useCallback((preceptor: MarkerPreceptor, options?: { hideName?: boolean }) => {
    if (!showTooltips || !mapRef.current) return;
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
      hideTooltipTimeoutRef.current = null;
    }
    clearTooltip();
    popupRef.current = new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat([preceptor.longitude!, preceptor.latitude!])
      .setDOMContent(createTooltipHTML(preceptor, options))
      .addTo(mapRef.current);

    setTimeout(() => {
      const popupContent = document.querySelector('.mapboxgl-popup-content');
      if (!popupContent) return;
      popupContent.addEventListener('mouseenter', () => {
        if (hideTooltipTimeoutRef.current) {
          clearTimeout(hideTooltipTimeoutRef.current);
          hideTooltipTimeoutRef.current = null;
        }
      });
      popupContent.addEventListener('mouseleave', scheduleHideTooltip);
    }, 0);
  }, [clearTooltip, scheduleHideTooltip, showTooltips, mapRef]);

  return { clearTooltip, scheduleHideTooltip, showTooltipForPreceptor, popupRef };
};
